import { SerializedError } from '@reduxjs/toolkit';
import { Dispatch, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { ApiError } from 'lib/errors';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

export type UseRTKError = Error | ApiError | SerializedError | null | undefined;
type ErrorCallback = (
  error: ApiResponseError,
) => { message: string; level?: MessageType } | undefined;

interface UseRTKErrorHandlerArgs {
  error: UseRTKError;
  onError?: ErrorCallback;
}

export const rtkErrorHandlerLogic = (
  error: UseRTKError,
  dispatch: Dispatch<any>,
  onError?: ErrorCallback,
) => {
  if (error == null) {
    // Do nothing
    return;
  }
  const isApiError = typeof error === 'object' && error != null && 'method' in error;
  if (!isApiError) {
    throw error;
  }

  if (error instanceof ApiNetworkError) {
    dispatch(
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    );
  } else if (error instanceof ApiResponseError) {
    if (isPermissionsError(error)) {
      dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
    } else {
      const displayableError = onError?.(error);
      if (displayableError) {
        dispatch(showMessage(displayableError.message, displayableError.level));
      }
    }
  } else {
    throw error;
  }
};

export const useLazyRTKErrorHandler = () => {
  const dispatch = useDispatch();

  const handleUseRTKError = ({ error, onError }: UseRTKErrorHandlerArgs) => {
    rtkErrorHandlerLogic(error, dispatch, onError);
  };

  return { handleUseRTKError };
};

/**
 * A hook that triggers an error message display when it receives an error.
 * Meant to be used in conjunction with RTK hooks to flash messages on error.
 */
export default ({ error, onError }: UseRTKErrorHandlerArgs) => {
  const dispatch = useDispatch();

  useEffect(() => {
    rtkErrorHandlerLogic(error, dispatch, onError);
  }, [error]);
};
